/**
 * Auth state management for the web panel.
 *
 * Zustand store with login/logout/checkAuth actions.
 * The login action calls the API with surface:'web' which causes the
 * backend to set httpOnly cookies. checkAuth is called on app mount
 * to verify session from existing cookies via a refresh attempt.
 */
import { create } from 'zustand';
import { loginApi, logoutApi, checkSession, fetchMe } from '../api/auth';
import { useTenantStore } from '../stores/tenant.store';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isLoading: true,

  login: async (email: string, password: string) => {
    await loginApi(email, password);
    set({ isAuthenticated: true });
    // Hydrate tenant store from backend (CR-05 fix)
    try {
      const me = await fetchMe();
      useTenantStore.getState().setTenant(me.organizationId, me.organizationName);
    } catch {
      // Best-effort: panel will still work but show "Sem organizacao"
    }
  },

  logout: async () => {
    await logoutApi();
    set({ isAuthenticated: false });
    useTenantStore.getState().clearTenant();
    window.location.href = '/login';
  },

  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const valid = await checkSession();
      if (valid) {
        // Hydrate tenant store on session revalidation too (CR-05 fix)
        try {
          const me = await fetchMe();
          useTenantStore.getState().setTenant(me.organizationId, me.organizationName);
        } catch {
          // Best-effort
        }
      }
      set({ isAuthenticated: valid, isLoading: false });
      return valid;
    } catch {
      set({ isAuthenticated: false, isLoading: false });
      return false;
    }
  },
}));
